import { TypedEventEmitter } from './event-emitter';
const isBrowser = typeof window !== 'undefined' && typeof RTCPeerConnection !== 'undefined';
export class WebRTCWrapper extends TypedEventEmitter {
    constructor(options = {}) {
        super({ debug: options.debug });
        this.pc = null;
        this.combinedStream = null;
        this.candidateQueue = [];
        this.remoteDescriptionSet = false;
        this.statsTimer = null;
        this.prevBytesReceived = 0;
        this.prevTimestamp = 0;
        this.primingRead = false;
        this.visibilityHandler = null;
        this.iceServers = options.iceServers ?? [];
        this.statsIntervalMs = options.statsIntervalMs ?? 2000;
        this.debug = options.debug ?? false;
    }
    get peerConnection() { return this.pc; }
    get iceState() { return this.pc?.iceConnectionState ?? 'new'; }
    get isRemoteDescriptionSet() { return this.remoteDescriptionSet; }
    log(...args) {
        if (this.debug)
            console.log('[WebRTC]', ...args);
    }
    createPeerConnection() {
        if (!isBrowser)
            throw new Error('WebRTC is not available in this environment');
        this.closePeerConnection();
        this.pc = new RTCPeerConnection({
            iceServers: this.iceServers.length > 0 ? this.iceServers : undefined,
        });
        this.pc.onicecandidate = (event) => this.emit('iceCandidate', event.candidate);
        this.pc.oniceconnectionstatechange = () => {
            const state = this.pc?.iceConnectionState;
            this.log('ICE state:', state);
            this.emit('iceStateChange', state);
        };
        this.pc.onconnectionstatechange = () => {
            const state = this.pc?.connectionState;
            if (state) {
                this.log('Connection state:', state);
                this.emit('connectionStateChange', state);
            }
        };
        this.pc.ontrack = (event) => {
            this.log('Track received:', event.track.kind);
            if (!this.combinedStream)
                this.combinedStream = new MediaStream();
            this.combinedStream.addTrack(event.track);
            this.emit('track', this.combinedStream);
        };
        this.remoteDescriptionSet = false;
        this.candidateQueue = [];
        this.combinedStream = null;
        return this.pc;
    }
    async setRemoteDescription(offer) {
        if (!this.pc)
            throw new Error('No PeerConnection');
        await this.pc.setRemoteDescription(offer);
        this.remoteDescriptionSet = true;
        if (offer.sdp?.includes('m=application')) {
            this.dataChannel = this.pc.createDataChannel('input');
            this.dataChannel.onopen = () => this.emit('dataChannelOpen', this.dataChannel);
            this.dataChannel.onclose = () => this.emit('dataChannelClose', undefined);
            this.dataChannel.onerror = (e) => this.log('Data channel error:', e);
        }
        for (const candidate of this.candidateQueue) {
            try {
                await this.pc.addIceCandidate(candidate);
            }
            catch (err) {
                this.log('Error adding queued candidate:', err);
            }
        }
        this.candidateQueue = [];
    }
    sendData(data) {
        if (this.dataChannel?.readyState === 'open')
            this.dataChannel.send(typeof data === 'string' ? data : JSON.stringify(data));
    }
    async createAnswer() {
        if (!this.pc)
            throw new Error('No PeerConnection');
        const answer = await this.pc.createAnswer();
        await this.pc.setLocalDescription(answer);
        return answer;
    }
    async addIceCandidate(candidate) {
        if (!this.pc)
            return;
        if (!this.remoteDescriptionSet) {
            if (candidate)
                this.candidateQueue.push(candidate);
            return;
        }
        try {
            await this.pc.addIceCandidate(candidate ?? undefined);
        }
        catch (err) {
            this.log('Error adding ICE candidate:', err);
        }
    }
    startStatsPolling() {
        this.stopStatsPolling();
        if (!isBrowser)
            return;
        this.primingRead = true;
        void this.collectStats().finally(() => { this.primingRead = false; });
        this.statsTimer = setInterval(() => this.collectStats(), this.statsIntervalMs);
        this.visibilityHandler = () => {
            if (document.hidden) {
                this.stopStatsTimer();
            }
            else {
                this.primingRead = true;
                void this.collectStats().finally(() => {
                    this.primingRead = false;
                    this.statsTimer = setInterval(() => this.collectStats(), this.statsIntervalMs);
                });
            }
        };
        document.addEventListener('visibilitychange', this.visibilityHandler);
    }
    stopStatsTimer() {
        if (this.statsTimer) {
            clearInterval(this.statsTimer);
            this.statsTimer = null;
        }
    }
    stopStatsPolling() {
        this.stopStatsTimer();
        if (this.visibilityHandler && isBrowser) {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
            this.visibilityHandler = null;
        }
        this.prevBytesReceived = 0;
        this.prevTimestamp = 0;
    }
    async collectStats() {
        if (!this.pc)
            return;
        try {
            const reports = await this.pc.getStats();
            let stats = null;
            reports.forEach((report) => {
                if (report.type === 'inbound-rtp' && report.kind === 'video') {
                    const now = report.timestamp;
                    const bytes = report.bytesReceived ?? 0;
                    const elapsed = this.prevTimestamp > 0 ? (now - this.prevTimestamp) / 1000 : 0;
                    stats = {
                        timestamp: now,
                        bytesReceived: bytes,
                        packetsReceived: report.packetsReceived ?? 0,
                        packetsLost: report.packetsLost ?? 0,
                        jitter: report.jitter ?? 0,
                        bitrate: elapsed > 0 ? Math.round(((bytes - this.prevBytesReceived) * 8) / elapsed) : 0,
                        framesDecoded: report.framesDecoded ?? 0,
                        framesDropped: report.framesDropped ?? 0,
                        frameWidth: report.frameWidth ?? 0,
                        frameHeight: report.frameHeight ?? 0,
                        framesPerSecond: report.framesPerSecond ?? 0,
                    };
                    this.prevBytesReceived = bytes;
                    this.prevTimestamp = now;
                }
            });
            if (stats && !this.primingRead)
                this.emit('stats', stats);
        }
        catch {
            // Stats collection failure is non-fatal
        }
    }
    closePeerConnection() {
        this.stopStatsPolling();
        this.combinedStream = null;
        if (this.dataChannel) {
            this.dataChannel.onopen = null;
            this.dataChannel.onclose = null;
            this.dataChannel.onerror = null;
            this.dataChannel = null;
        }
        if (this.pc) {
            this.pc.onicecandidate = null;
            this.pc.oniceconnectionstatechange = null;
            this.pc.onconnectionstatechange = null;
            this.pc.ontrack = null;
            this.pc.close();
            this.pc = null;
        }
        this.remoteDescriptionSet = false;
        this.candidateQueue = [];
    }
    dispose() {
        this.closePeerConnection();
        this.removeAllListeners();
    }
}
